import { useParams, Link } from "react-router-dom";
import { useMemo, useEffect } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allArticles, categories } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => allArticles.find((a) => a.slug === slug),
    [slug]
  );

  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : null),
    [article]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，该文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const renderContent = (content: string) => {
    const lines = content.split("\n");
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith("## ")) {
        elements.push(
          <h2 key={i} className="text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
            {line.replace("## ", "")}
          </h2>
        );
        i++;
      } else if (line.startsWith("### ")) {
        elements.push(
          <h3 key={i} className="text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3">
            {line.replace("### ", "")}
          </h3>
        );
        i++;
      } else if (line.startsWith("**") && line.endsWith("**")) {
        elements.push(
          <p key={i} className="text-base leading-relaxed text-foreground-800 font-semibold mb-4">
            {line.replace(/\*\*/g, "")}
          </p>
        );
        i++;
      } else if (line.startsWith("- **")) {
        elements.push(
          <p key={i} className="text-sm leading-relaxed text-foreground-700 ml-4 mb-2">
            <strong>{line.replace(/^- \*\*/, "").replace(/\*\*.*/, "")}</strong>
            {line.replace(/^-\s*\*\*[^*]+\*\*/, "")}
          </p>
        );
        i++;
      } else if (line.startsWith("- ")) {
        elements.push(
          <p key={i} className="text-sm leading-relaxed text-foreground-700 ml-4 mb-1">
            {line}
          </p>
        );
        i++;
      } else if (line.trim() === "") {
        elements.push(<div key={i} className="h-4"></div>);
        i++;
      } else if (line.startsWith("> ")) {
        elements.push(
          <blockquote key={i} className="border-l-4 border-primary-300 bg-primary-50/50 pl-4 py-3 my-4 rounded-r-lg">
            <p className="text-sm leading-relaxed text-foreground-700 italic">
              {line.replace("> ", "")}
            </p>
          </blockquote>
        );
        i++;
      } else {
        elements.push(
          <p key={i} className="text-sm md:text-base leading-relaxed text-foreground-800 mb-4">
            {line}
          </p>
        );
        i++;
      }
    }

    return elements;
  };

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://www.dapiguzuowen.com/article/${article.slug}`,
    publisher: {
      "@type": "EducationalOrganization",
      name: "打屁股作文",
      url: "https://www.dapiguzuowen.com",
    },
    articleSection: category?.name || "",
    keywords: article.tags.join(", "),
    inLanguage: "zh-CN",
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <main className="pt-16 bg-background-50">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: category?.name || "分类", to: category ? `/category/${category.slug}` : undefined },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8 md:py-12">
          <header className="mb-8 md:mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {category?.name}
              </Link>
              {article.tags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 阅读约{article.readTime}
              </span>
            </div>
          </header>

          <div className="prose-custom">
            {renderContent(article.content)}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/60">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-700 rounded-full text-xs border border-background-200/60"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关文章推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2 flex-1">
                        {ra.excerpt}
                      </p>
                      <div className="text-xs text-foreground-400">
                        {ra.author} · {ra.date}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}